import { useBlocNext } from "@blac/react";
import { CSSProperties, FC } from "react";
import {
  IconClose,
  IconSophieStepClock,
  IconSophieStepPeople,
  IconSophieStepSparkles
} from "src/constants/icons";
import translate from "src/lib/translate";
import { BottomSheet } from "src/ui/components/BottomSheet/BottomSheet";
import Translate from "src/ui/components/Translate/Translate";
import SophieIntroCardCubit from "./SophieIntroCardCubit";
import {
  DesktopCloseButton,
  DesktopTitle,
  DesktopTitleBar,
  HeroImage,
  HeroImageCurve,
  HeroImageWrapper,
  IntroCard,
  IntroCardCloseButton,
  IntroCardDescription,
  IntroCardLearnMore,
  IntroCardTitle,
  IntroCardWrapper,
  SheetCloseButton,
  SheetTitle,
  StepBody,
  StepDescription,
  StepIconWrap,
  StepItem,
  StepList,
  StepTitle
} from "./SophieIntroCard.styled";

const SHEET_STYLE = {
  "--dialog-background": "#ffffff"
} as CSSProperties;

const HERO_IMAGE_URL =
  "https://cdn.sanity.io/images/g38rxyoc/production/03ce835a844d6f3c7969b96ae50c54ec9f84671f-981x600.png";

const LearnMoreIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5.83325 15L15.8333 5M15.8333 11.6667V5H9.16659"
      stroke="#5F1F8F"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SOPHIE_TRACKING_NAME = "Sophie Intro Sheet";

const SophieIntroCard: FC = () => {
  const [
    { isDismissed, isSheetOpen, hasSheetBeenOpened },
    { dismiss, openSheet, closeSheet }
  ] = useBlocNext(SophieIntroCardCubit);

  if (isDismissed) {
    return null;
  }

  return (
    <IntroCardWrapper>
      <IntroCard
        role="region"
        aria-labelledby="sophie-intro-title"
        aria-describedby="sophie-intro-description"
      >
        <IntroCardCloseButton
          type="button"
          onClick={dismiss}
          aria-label={translate("leapChat.sophieIntro.card.dismiss")}
        >
          <IconClose />
        </IntroCardCloseButton>
        <IntroCardTitle id="sophie-intro-title">
          <Translate msg="leapChat.sophieIntro.card.title" />
        </IntroCardTitle>
        <IntroCardDescription id="sophie-intro-description">
          <Translate msg="leapChat.sophieIntro.card.description" />
        </IntroCardDescription>
        <IntroCardLearnMore type="button" onClick={openSheet}>
          <Translate msg="leapChat.sophieIntro.card.learnMore" />
          <LearnMoreIcon />
        </IntroCardLearnMore>
      </IntroCard>
      <BottomSheet
        isOpen={isSheetOpen}
        onClose={closeSheet}
        trackingName={hasSheetBeenOpened ? SOPHIE_TRACKING_NAME : undefined}
        style={SHEET_STYLE}
        overlayDragHandle
        desktopFullscreen
        desktopHeader={
          <DesktopTitleBar>
            <DesktopTitle>
              <Translate msg="leapChat.sophieIntro.sheet.title" />
            </DesktopTitle>
            <DesktopCloseButton
              type="button"
              onClick={closeSheet}
              aria-label={translate("close")}
            >
              <IconClose />
            </DesktopCloseButton>
          </DesktopTitleBar>
        }
      >
        <SheetCloseButton
          type="button"
          onClick={closeSheet}
          aria-label={translate("close")}
        >
          <IconClose />
        </SheetCloseButton>
        <HeroImageWrapper>
          <HeroImage src={HERO_IMAGE_URL} alt="" />
          <HeroImageCurve
            viewBox="0 0 100 30"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M0,30 Q50,-30 100,30 Z" fill="#ffffff" />
          </HeroImageCurve>
        </HeroImageWrapper>
        <SheetTitle>
          <Translate msg="leapChat.sophieIntro.sheet.title" />
        </SheetTitle>
        <StepList>
          <StepItem>
            <StepIconWrap>
              <IconSophieStepSparkles />
            </StepIconWrap>
            <StepBody>
              <StepTitle>
                <Translate msg="leapChat.sophieIntro.sheet.step1.title" />
              </StepTitle>
              <StepDescription>
                <Translate msg="leapChat.sophieIntro.sheet.step1.description" />
              </StepDescription>
            </StepBody>
          </StepItem>
          <StepItem>
            <StepIconWrap>
              <IconSophieStepPeople />
            </StepIconWrap>
            <StepBody>
              <StepTitle>
                <Translate msg="leapChat.sophieIntro.sheet.step2.title" />
              </StepTitle>
              <StepDescription>
                <Translate msg="leapChat.sophieIntro.sheet.step2.description" />
              </StepDescription>
            </StepBody>
          </StepItem>
          <StepItem>
            <StepIconWrap>
              <IconSophieStepClock />
            </StepIconWrap>
            <StepBody>
              <StepTitle>
                <Translate msg="leapChat.sophieIntro.sheet.step3.title" />
              </StepTitle>
              <StepDescription>
                <Translate msg="leapChat.sophieIntro.sheet.step3.description" />
              </StepDescription>
            </StepBody>
          </StepItem>
        </StepList>
      </BottomSheet>
    </IntroCardWrapper>
  );
};

export default SophieIntroCard;
